/* eslint-disable no-param-reassign */
/* eslint-disable import/no-unresolved */

/**
 * @fileoverview Componente PreviewFile optimizado para previsualización de archivos multimedia con soporte para PDF, imágenes y videos, gestión eficiente de memoria y renderizado optimizado.
 * @module PreviewFile
 * @category Component - Atom
 * @since 1.0.0
 * @version 3.1.0 - Optimizaciones de performance, memoización estratégica y gestión eficiente de recursos.
 *
 * @typedef {Object} PreviewFileProps
 * @description Propiedades del componente PreviewFile para configuración de previsualización
 * @property {File} selectedFile - (Obligatorio) Objeto File a previsualizar (imagen, PDF o video)
 * @property {Object} [containerStyle] - Estilos CSS aplicados al contenedor principal de previsualización
 * @property {boolean} [simplePdf=false] - Modo simple para PDFs: solo muestra preview sin detalles adicionales
 */

import React, { useEffect, useState, useCallback, useMemo, memo, useRef } from "react";
import { isEmpty } from "lodash";
import { useTranslation } from 'react-i18next';
import pdfWs from 'images/pdf-ws.png';

import fileUtils from '../../util/fileUtils';

const pdfjs = require('pdfjs-dist/legacy/build/pdf');

// Configuración memoizada del worker de PDF para evitar recreaciones
let pdfWorkerBlob = null;
const getPdfWorkerSrc = () => {
  if (!pdfWorkerBlob) {
    pdfWorkerBlob = URL.createObjectURL(
      // eslint-disable-next-line global-require
      new Blob([`importScripts('${require('pdfjs-dist/legacy/build/pdf.worker.mjs')}');`], {
        type: 'application/javascript'
      })
    );
  }
  return pdfWorkerBlob;
};

pdfjs.GlobalWorkerOptions.workerSrc = getPdfWorkerSrc();

/**
 * Componente Atom para previsualización optimizada de archivos multimedia.
 *
 * Maneja la previsualización de imágenes, PDFs y videos con gestión eficiente de memoria,
 * renderizado optimizado y soporte para diferentes modos de visualización.
 *
 * @component PreviewFile
 * @category Component - Atom
 *
 * @description
 * **Arquitectura Optimizada (v3.1.0):**
 * - ✅ **Memory Management**: Gestión automática de ObjectURLs para evitar memory leaks
 * - ✅ **PDF Processing**: Renderizado eficiente de PDFs con worker memoizado
 * - ✅ **Image Optimization**: Cálculo inteligente de dimensiones y estilos
 * - ✅ **Performance**: Memoización estratégica de cálculos costosos
 * - ✅ **Responsive**: Adaptación automática a diferentes tamaños de contenedor
 *
 * @param {PreviewFileProps} props - Propiedades de configuración del componente
 * @returns {JSX.Element} Elemento JSX con previsualización del archivo
 *
 * @example
 * ```jsx
 * // Previsualización simple de imagen
 * <PreviewFile 
 *   selectedFile={imageFile} 
 *   containerStyle={{ width: 100, height: 100 }}
 * />
 * ```
 *
 * @example
 * ```jsx
 * // Previsualización simple de PDF
 * <PreviewFile 
 *   selectedFile={pdfFile} 
 *   simplePdf={true}
 *   containerStyle={{ width: 50, height: 50 }}
 * />
 * ```
 */
const PreviewFile = memo(({
  selectedFile,
  containerStyle: propContainerStyle,
  simplePdf = false
}) => {
  const { t } = useTranslation('NewChat');
  
  // Estados optimizados con valores iniciales eficientes
  const [fileToImage, setFileToImage] = useState(null);
  const [fileTextPages, setFileTextPages] = useState('');
  const [imageSize, setImageSize] = useState(null);
  
  // Ref para tracking de cleanup
  const urlCleanupRef = useRef(null);

  // Propiedades básicas del archivo memoizadas
  const fileName = useMemo(() => selectedFile?.name || '', [selectedFile]);
  const fileSize = useMemo(() => {
    if (selectedFile) return fileUtils.formatFileSize(selectedFile.size);
    return '0';
  }, [selectedFile]);

  // URL del archivo creada de forma tradicional para mantener funcionalidad
  const fileUrl = useMemo(() => {
    if (selectedFile) {
      const url = URL.createObjectURL(selectedFile);
      urlCleanupRef.current = url;
      return url;
    }
    return null;
  }, [selectedFile]);

  // Tipos de archivo memoizados
  const fileTypes = useMemo(() => ({
    isImage: selectedFile?.type.startsWith('image/') || false,
    isPdf: selectedFile?.type === 'application/pdf' || false,
    isVideo: selectedFile ? ['video/mp4', 'video/3gpp'].includes(selectedFile.type) : false
  }), [selectedFile]);

  const { isImage: isImageFile, isPdf: isPdfFile, isVideo: isVideoFile } = fileTypes;

  const renderPDFToCanvas = useCallback(async (pdfUrl, pageNum, canvasElement) => {
    const loadingTask = pdfjs.getDocument(pdfUrl);
    const pdfDocument = await loadingTask.promise;

    const text = pdfDocument.numPages > 1 ? t('previewFile.pages') : t('previewFile.page');
    setFileTextPages(`${pdfDocument.numPages} ${text}`);

    const page = await pdfDocument.getPage(pageNum);

    const viewport = page.getViewport({ scale: 1 });
    canvasElement.width = viewport.width;
    canvasElement.height = viewport.height;

    const canvasContext = canvasElement.getContext('2d');
    const renderContext = {
      canvasContext,
      viewport
    };

    await page.render(renderContext).promise;
  }, [t]);

  const handleImageSize = useCallback((file, url) => {
    if (!file || !file.type.startsWith('image/')) {
      setImageSize(null);
      return;
    }

    const img = new Image();
    
    const handleLoad = () => {
      setImageSize({
        width: img.width,
        height: img.height
      });
      // Cleanup event listeners
      img.onload = null;
      img.onerror = null;
    };

    const handleError = () => {
      setImageSize({ width: 200, height: 200 }); // Fallback size
      // Cleanup event listeners
      img.onload = null;
      img.onerror = null;
    };

    img.onload = handleLoad;
    img.onerror = handleError;
    img.src = url;
  }, []);

  const convertPdfFileToImage = useCallback(async () => {
    if (!selectedFile || !isPdfFile) return;

    const pdfUrl = URL.createObjectURL(selectedFile);
    const pageNumber = 1;
    const canvasElement = document.createElement('canvas');

    try {
      await renderPDFToCanvas(pdfUrl, pageNumber, canvasElement);
      const imgDataUrl = canvasElement.toDataURL('image/png', 0.8); // Optimizar calidad/tamaño
      setFileToImage(imgDataUrl);
    } catch (error) {
      console.error('Error converting PDF to image:', error);
      setFileToImage(null);
    } finally {
      URL.revokeObjectURL(pdfUrl);
      // Cleanup canvas
      canvasElement.width = 0;
      canvasElement.height = 0;
    }
  }, [selectedFile, isPdfFile, renderPDFToCanvas]);

  // Cleanup de URL cuando el componente se desmonta o el archivo cambia
  useEffect(() => {
    return () => {
      if (urlCleanupRef.current) {
        URL.revokeObjectURL(urlCleanupRef.current);
        urlCleanupRef.current = null;
      }
    };
  }, [fileUrl]);

  useEffect(() => {
    // Reset state cuando no hay archivo
    if (!selectedFile) {
      setImageSize(null);
      setFileToImage(null);
      setFileTextPages('');
      return;
    }

    // Handle image files - crear URL localmente para evitar problemas de timing
    if (isImageFile) {
      const url = URL.createObjectURL(selectedFile);
      handleImageSize(selectedFile, url);
      // No cleanup aquí porque fileUrl principal se encarga
    }

    // Handle PDF files
    if (isPdfFile) {
      convertPdfFileToImage();
    }
  }, [selectedFile, isImageFile, isPdfFile, handleImageSize, convertPdfFileToImage]);

  const renderPdfFile = useCallback(() => {
    if (simplePdf) {
      // Modo simple: solo mostrar el preview de la primera página
      return (
        <div className="tw-w-full tw-h-full tw-overflow-hidden tw-relative" style={propContainerStyle}>
          {fileToImage && (
            <img
              src={fileToImage}
              className="tw-w-full tw-h-full tw-object-cover tw-rounded"
              alt={fileName || 'PDF preview'}
            />
          )}
          <div className="tw-absolute tw-bottom-1 tw-left-1 tw-bg-white tw-rounded-sm tw-p-0.5 tw-shadow-sm">
            <img src={pdfWs} title={t('previewFile.pdfTitle')} className="tw-w-[10px] tw-h-[12px]" alt="PDF icon" />
          </div>
        </div>
      );
    }

    // Modo completo con detalles
    return (
      <div className="tw-w-full tw-box-border">
        <div className="tw-text-inherit tw-overflow-y-hidden tw-flex tw-w-full tw-overflow-x-hidden tw-flex-col tw-items-stretch tw-box-border">
          <div className="tw-w-full tw-relative tw-h-[100px]">
            <div
              className="!tw-bg-no-repeat !tw-bg-cover tw-h-full tw-w-full tw-absolute tw-bg-left-top"
              style={{ backgroundImage: `url(${fileToImage})` }}
            />
          </div>
        </div>
        <div className="tw-rounded-tr-md tw-bg-[#d1f4cc] tw-overflow-y-hidden tw-w-full tw-overflow-x-hidden tw-no-underline">
          <div className="tw-py-[13px] tw-px-[19px] tw-flex tw-flex-row tw-items-start tw-justify-start tw-flex-nowrap tw-min-h-0">
            <img src={pdfWs} title={t('previewFile.pdfTitle')} className="tw-w-[26px] tw-h-[30px]" alt="PDF icon" />
            <div className="tw-flex tw-flex-col tw-my-0 tw-mx-[10px]">
              <span className="tw-line-clamp-2 tw-overflow-y-hidden tw-break-all tw-text-ellipsis tw-overflow-x-hidden tw-block tw-text-xs">
                {fileName}
              </span>
              <div className="tw-text-[#8696a0] tw-py-0 tw-px-[3px] tw-text-[0.6875rem] tw-h-[15px] tw-leading-[15px]">
                <span>{fileTextPages}</span>
                <span className="tw-ml-1 tw-mr-1 tw-mt-0 tw-overflow-y-hidden tw-mb-0 tw-text-ellipsis tw-whitespace-nowrap tw-inline-block tw-overflow-x-hidden tw-font-bold">.</span>
                <span>PDF</span>
                <span className="tw-ml-1 tw-mr-1 tw-mt-0 tw-overflow-y-hidden tw-mb-0 tw-text-ellipsis tw-whitespace-nowrap tw-inline-block tw-overflow-x-hidden tw-font-bold">.</span>
                <span>{fileSize}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }, [simplePdf, propContainerStyle, fileToImage, fileName, t, fileTextPages, fileSize]);

  const containerStyle = useMemo(() => {
    // Si se pasa containerStyle como prop, usar ese (caso DropzoneFileUploader)
    if (propContainerStyle) {
      return propContainerStyle;
    }

    // Validación optimizada de imageSize
    if (!imageSize || isEmpty(imageSize)) {
      return {};
    }

    const { width, height } = imageSize;

    // Cálculo optimizado de dimensiones
    if (width > 330) {
      const calculatedHeight = height > 235 ? 82.5 + ((height - 235) * 0.15) : 82.5;
      return {
        width: '330px',
        height: `${calculatedHeight}px`
      };
    }

    return {
      width: '240px',
      height: '313.5px'
    };
  }, [propContainerStyle, imageSize]);

  const imageSizeFlags = useMemo(() => ({
    isLarge: imageSize?.width > 330,
    isSmall: imageSize?.width < 240
  }), [imageSize]);
  
  const { isLarge: isLargeImage, isSmall: isSmallImage } = imageSizeFlags;

  const renderImage = useCallback(() => {
    if (!imageSize || isEmpty(imageSize) || !fileUrl) return null;

    if (isLargeImage) {
      return (
        <div className="tw-overflow-y-clip tw-overflow-x-clip" style={containerStyle}>
          <div
            className="tw-rounded-md tw-h-full !tw-bg-center tw-scale-100 !tw-bg-cover !tw-bg-no-repeat tw-z-[1]"
            style={{ background: `url(${fileUrl})` }}
            aria-label={fileName || 'Preview image'}
          />
        </div>
      );
    }

    return (
      <div className="tw-overflow-y-clip tw-overflow-x-clip">
        <div className="tw-z-[100] tw-overflow-y-clip tw-overflow-x-clip tw-scale-110" style={containerStyle}>
          <img
            src={fileUrl}
            className={`tw-rounded-[10px] ${isSmallImage ? 'tw-w-full' : ''}`}
            alt={fileName || 'Preview image'}
          />
        </div>
      </div>
    );
  }, [imageSize, fileUrl, containerStyle, isLargeImage, isSmallImage, fileName]);

  // Los tipos de archivo ya están memoizados en fileInfo, no necesitamos duplicar

  const renderPreviewFile = useCallback(() => {
    // Early return optimizado
    if (!selectedFile || !fileUrl) return null;

    // Renderizado de PDF
    if (isPdfFile) {
      return fileToImage ? renderPdfFile() : null;
    }

    // Renderizado de Video
    if (isVideoFile) {
      return (
        <video
          type="video/webm"
          src={fileUrl}
          width="100%"
          height="200"
          controls
          preload="metadata"
          aria-label={`Video: ${fileName}`}
        />
      );
    }

    // Renderizado de Imagen optimizado
    if (isImageFile) {
      // Caso específico para DropzoneFileUploader (con propContainerStyle)
      if (propContainerStyle) {
        return (
          <div className="tw-overflow-hidden" style={propContainerStyle}>
            <img
              src={fileUrl}
              className="tw-w-full tw-h-full tw-object-cover"
              alt={fileName || 'Preview image'}
              loading="lazy"
            />
          </div>
        );
      }
      
      // Caso normal con imageSize calculado
      if (imageSize && !isEmpty(imageSize)) {
        return renderImage();
      }
      
      // Fallback optimizado
      return (
        <div className="tw-overflow-hidden">
          <img
            src={fileUrl}
            className="tw-rounded-[10px] tw-max-w-full tw-max-h-full tw-object-cover"
            alt={fileName || 'Preview image'}
            loading="lazy"
          />
        </div>
      );
    }

    return null;
  }, [selectedFile, fileUrl, isPdfFile, fileToImage, renderPdfFile, isVideoFile, fileName, isImageFile, imageSize, renderImage, propContainerStyle]);

  return renderPreviewFile();
});

PreviewFile.displayName = 'PreviewFile';

export default PreviewFile;
